import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import "../styles/LoginPage.css";
import SEO from "../components/SEO";
import api, { API_ENDPOINTS } from "../config/api";
import { useAuth } from "../context/AuthContext";

const LoginPage = () => {
  const { login } = useAuth();
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {}, []);

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      if (isLoading) {
        return;
      }

      setIsLoading(true);
      setErrorMessage("");

      const response = await api.post(API_ENDPOINTS.AUTH.VERIFY_GOOGLE_TOKEN, {
        token: credentialResponse.credential,
      });

      if (response.data && response.data.user) {
        // Check if we're on Safari or in production
        const isSafari =
          /Safari/.test(navigator.userAgent) &&
          !/Chrome/.test(navigator.userAgent);
        const isProduction = process.env.NODE_ENV === "production";

        // Handle Safari/Production fallback tokens
        if (response.data.accessToken && response.data.refreshToken) {
          localStorage.setItem("accessToken", response.data.accessToken);
          localStorage.setItem("refreshToken", response.data.refreshToken);
        } else if (isSafari || isProduction) {
        }

        login(response.data.user);
        navigate("/");
      } else {
        console.error("❌ Invalid response format:", response.data);
        setErrorMessage("Login failed. Please try again.");
      }
    } catch (error) {
      console.error("❌ Google login error:", error);
      console.error("❌ Error response:", error.response?.data);
      console.error("❌ Error status:", error.response?.status);

      if (error.response?.status === 429) {
        setErrorMessage(
          "Too many login attempts. Please wait a few minutes and try again."
        );
      } else {
        setErrorMessage(
          error.response?.data?.message ||
            "Google login failed. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = (error) => {
    console.error("❌ Google OAuth error:", error);
    setErrorMessage("Google login failed. Please try again.");
  };

  const handleGoogleClick = () => {};

  const testBackendConnection = async () => {
    try {
      setErrorMessage("");

      const response = await api.get("/api/auth/test-config");

      setErrorMessage(
        "Backend connection successful! Google OAuth should work."
      );
      setTimeout(() => setErrorMessage(""), 3000);
    } catch (error) {
      setErrorMessage("Backend connection failed. Please check server status.");
    }
  };

  return (
    <div className="login-page">
      <SEO
        title="Login - Grad4Grad | Access Detailed University Information"
        description="Sign in to Grad4Grad to access detailed university information, admission requirements, and personalized study abroad guidance. Secure Google OAuth login for international students."
        keywords="grad4grad login, university information access, study abroad login, international student portal, secure login, Google OAuth"
        image="https://grad4grad.com/logo512.png"
        url="https://grad4grad.com/login"
        type="website"
      />
      <div className="login-container">
        <div className="login-header">
          <div className="login-logo">
            <img
              src="/logo.png"
              alt="Grad4Grad Logo"
              className="login-logo-image"
            />
          </div>
          <h1>Welcome Back</h1>
          <p>Sign in to access detailed university information</p>
        </div>

        <div className="login-form">
          {errorMessage && (
            <div className="error-message">
              <p>{errorMessage}</p>
            </div>
          )}

          <div className="google-login-section">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              disabled={isLoading}
              onClick={handleGoogleClick}
            />
            {isLoading && (
              <div className="loading-indicator">
                <p>Signing you in...</p>
              </div>
            )}
          </div>

          <div className="login-footer">
            <div className="legal-consent">
              <label className="consent-checkbox">
                <input type="checkbox" defaultChecked />
                <span className="checkmark"></span>
                <span className="consent-text">
                  I agree to the{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                </span>
              </label>
            </div>
            <p className="legal-note">
              By signing in with Google, you acknowledge that you have read and
              agree to our terms and privacy policy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
